
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Crown, Users } from 'lucide-react';
import { format } from 'date-fns';

interface CommunityMessage {
  id: string;
  community_id: string;
  user_id: string;
  content: string;
  message_type: string;
  created_at: string;
  profiles?: {
    id: string;
    full_name?: string;
    username?: string;
    avatar_url?: string;
  };
}

interface StartupCommunityChatProps {
  communityId: string;
  communityName: string;
  isAdmin?: boolean;
}

const StartupCommunityChat: React.FC<StartupCommunityChatProps> = ({ 
  communityId, 
  communityName, 
  isAdmin = false 
}) => {
  const [messages, setMessages] = useState<CommunityMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('startup_community_messages')
        .select(`
          *,
          profiles:user_id (id, full_name, username, avatar_url)
        `)
        .eq('community_id', communityId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        throw error;
      }

      setMessages(data || []);
    } catch (error) {
      console.error('Error in fetchMessages:', error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!user || !newMessage.trim() || sending) return;

    try {
      setSending(true);
      
      const { error } = await supabase
        .from('startup_community_messages')
        .insert({
          community_id: communityId,
          user_id: user.id,
          content: newMessage.trim(),
          message_type: 'text'
        });

      if (error) {
        console.error('Error sending message:', error);
        throw error;
      }

      setNewMessage('');
      await fetchMessages();
    } catch (error) {
      console.error('Error in sendMessage:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    if (!communityId || !user) return;

    fetchMessages();

    // Set up realtime subscription
    const channel = supabase
      .channel(`startup_community_messages_${communityId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'startup_community_messages',
        filter: `community_id=eq.${communityId}`
      }, () => {
        fetchMessages();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [communityId, user]);

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Please log in to access the community chat.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="flex-shrink-0 border-b bg-gradient-to-r from-[#FF8C00]/10 to-orange-500/10">
        <CardTitle className="flex items-center gap-3">
          <MessageSquare className="h-5 w-5 text-[#FF8C00]" />
          <span>{communityName} Chat</span>
          {isAdmin && (
            <Badge className="bg-[#FF8C00] text-white">
              <Crown className="h-3 w-3 mr-1" />
              Admin
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <div className="text-muted-foreground mb-2">No messages yet</div>
              <div className="text-sm text-muted-foreground">Start the conversation!</div>
            </div>
          ) : (
            messages.map((message, index) => {
              const previousMessage = index > 0 ? messages[index - 1] : null;
              const showAuthor = !previousMessage || previousMessage.user_id !== message.user_id;
              const isOwn = message.user_id === user?.id;
              
              return (
                <div key={message.id} className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                  {!isOwn && showAuthor && (
                    <Avatar className="h-8 w-8 mt-1">
                      <AvatarImage src={message.profiles?.avatar_url || ""} alt="Avatar" />
                      <AvatarFallback>
                        {message.profiles?.full_name?.charAt(0) || message.profiles?.username?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  
                  {!isOwn && !showAuthor && (
                    <div className="w-8" />
                  )}
                  
                  <div className={`flex flex-col max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                    {!isOwn && showAuthor && (
                      <div className="text-xs text-muted-foreground mb-1 px-3">
                        {message.profiles?.full_name || message.profiles?.username || "Anonymous"}
                      </div>
                    )}
                    
                    <div
                      className={`rounded-2xl px-4 py-2 break-words ${
                        isOwn
                          ? 'bg-[#FF8C00] text-white rounded-br-md'
                          : 'bg-muted rounded-bl-md'
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                      <div className={`text-xs mt-1 opacity-70 ${isOwn ? 'text-right' : 'text-left'}`}>
                        {format(new Date(message.created_at), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Message Input */}
        <div className="flex-shrink-0 border-t p-4 bg-gray-50">
          <div className="flex gap-2">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1 min-h-[40px] max-h-[120px] resize-none"
              disabled={sending}
              rows={1}
            />
            <Button 
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="self-end bg-[#FF8C00] hover:bg-orange-600 text-white"
              size="sm"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StartupCommunityChat;
